import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Well } from '../types';

interface AppState {
  // County filter (null = All Counties)
  selectedCounty: string | null;
  setSelectedCounty: (county: string | null) => void;

  // Search
  searchQuery: string;
  setSearchQuery: (query: string) => void;

  // Favorites - now stores full Well objects
  favorites: Well[];
  addFavorite: (well: Well) => void;
  removeFavorite: (wellId: string) => void;
  isFavorite: (wellId: string) => boolean;

  // Selected well
  selectedWell: Well | null;
  setSelectedWell: (well: Well | null) => void;

  // User subscription
  subscriptionTier: 'free' | 'basic' | 'premium';
  setSubscriptionTier: (tier: 'free' | 'basic' | 'premium') => void;

  // Offline mode
  isOffline: boolean;
  setIsOffline: (offline: boolean) => void;
  
  // Simulate offline mode for testing (blocks all network requests)
  simulateOffline: boolean;
  setSimulateOffline: (simulate: boolean) => void;

  // Voice feedback
  voiceFeedbackEnabled: boolean;
  setVoiceFeedbackEnabled: (enabled: boolean) => void;

  // Nearby location from bot
  nearbyLocation: { lat: number; lng: number } | null;
  setNearbyLocation: (location: { lat: number; lng: number } | null) => void;

  // Shared route photo ID (from URL param ?route=123)
  sharedRoutePhotoId: number | null;
  setSharedRoutePhotoId: (id: number | null) => void;

  // Shared facility ID (from URL param ?facility=xxx)
  sharedFacilityId: string | null;
  setSharedFacilityId: (id: string | null) => void;

  // Shared inspection ID (from URL param ?inspection=xxx or startapp=inspection_xxx)
  sharedInspectionId: string | null;
  setSharedInspectionId: (id: string | null) => void;

  // Shared JSA ID (from startapp=jsa_xxx deep link)
  sharedJsaId: string | null;
  setSharedJsaId: (id: string | null) => void;
  pendingTrackId: string | null;
  setPendingTrackId: (id: string | null) => void;

  // Pending JSA geofence prompt ID (from startapp=jsa_prompt_xxx)
  pendingJsaPromptId: string | null;
  setPendingJsaPromptId: (id: string | null) => void;

  // Pending company meeting ID (from startapp=meeting_xxx)
  pendingMeetingId: string | null;
  setPendingMeetingId: (id: string | null) => void;

  // Favorites map visibility (global trigger)
  showFavoritesMap: boolean;
  setShowFavoritesMap: (show: boolean) => void;

  // Theme mode: 'light', 'dark', or 'auto' (switches based on time of day)
  themeMode: 'light' | 'dark' | 'auto';
  setThemeMode: (mode: 'light' | 'dark' | 'auto') => void;
  // Computed dark mode based on themeMode and time
  darkMode: boolean;
  toggleDarkMode: () => void;
  // Helper to check if it's currently night time (6pm-6am)
  isNightTime: () => boolean;

  // High contrast mode for outdoor/desert visibility
  highContrast: boolean;
  toggleHighContrast: () => void;

  // Language (English/Spanish)
  language: 'en' | 'es';
  setLanguage: (language: 'en' | 'es') => void;
  toggleLanguage: () => void;

  // Location sharing mode
  locationSharingMode: 'always' | 'while_using' | 'ask' | 'never';
  setLocationSharingMode: (mode: 'always' | 'while_using' | 'ask' | 'never') => void;

  // User vehicle type
  userVehicle: 'dot' | 'tractor' | 'semi' | 'welding' | 'pickup' | 'company' | 'vacuum' | 'rig' | 'car' | 'safety' | 'water' | 'hotOiler' | 'oilTruck' | 'poop' | 'eagle' | 'fracking' | 'drilling';
  setUserVehicle: (vehicle: 'dot' | 'tractor' | 'semi' | 'welding' | 'pickup' | 'company' | 'vacuum' | 'rig' | 'car' | 'safety' | 'water' | 'hotOiler' | 'oilTruck' | 'poop' | 'eagle' | 'fracking' | 'drilling') => void;
}

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      // County filter
      selectedCounty: null, // Default to All Counties
      setSelectedCounty: (county) => set({ selectedCounty: county }),

      // Search
      searchQuery: '',
      setSearchQuery: (query) => set({ searchQuery: query }),

      // Favorites - now stores full Well objects
      favorites: [],
      addFavorite: (well) => set((state) => {
        const existingIndex = state.favorites.findIndex(f => f.id === well.id);
        if (existingIndex >= 0) {
          const next = [...state.favorites];
          next[existingIndex] = well;
          return { favorites: next };
        }
        return { favorites: [...state.favorites, well] };
      }),
      removeFavorite: (wellId) => set((state) => ({ 
        favorites: state.favorites.filter(w => w.id !== wellId) 
      })),
      isFavorite: (wellId) => get().favorites.some(w => w.id === wellId),

      // Selected well
      selectedWell: null,
      setSelectedWell: (well) => set({ selectedWell: well }),

      // Subscription
      subscriptionTier: 'free',
      setSubscriptionTier: (tier) => set({ subscriptionTier: tier }),

      // Offline mode
      isOffline: !navigator.onLine,
      setIsOffline: (offline) => set({ isOffline: offline }),
      
      // Simulate offline mode for testing
      simulateOffline: false,
      setSimulateOffline: (simulate) => set({ 
        simulateOffline: simulate,
        // Also set isOffline when simulating so UI updates properly
        isOffline: simulate ? true : !navigator.onLine
      }),

      // Voice feedback
      voiceFeedbackEnabled: true,
      setVoiceFeedbackEnabled: (enabled) => set({ voiceFeedbackEnabled: enabled }),

      // Nearby location
      nearbyLocation: null,
      setNearbyLocation: (location) => set({ nearbyLocation: location }),

      // Shared route photo ID
      sharedRoutePhotoId: null,
      setSharedRoutePhotoId: (id) => set({ sharedRoutePhotoId: id }),

      // Shared facility ID
      sharedFacilityId: null,
      setSharedFacilityId: (id) => set({ sharedFacilityId: id }),

      // Shared inspection ID
      sharedInspectionId: null,
      setSharedInspectionId: (id) => set({ sharedInspectionId: id }),

      // Shared JSA ID
      sharedJsaId: null,
      setSharedJsaId: (id) => set({ sharedJsaId: id }),

      // Shared race track ID (VAMOS AR track invites)
      pendingTrackId: null,
      setPendingTrackId: (id) => set({ pendingTrackId: id }),

      // Pending JSA prompt ID
      pendingJsaPromptId: null,
      setPendingJsaPromptId: (id) => set({ pendingJsaPromptId: id }),

      // Pending company meeting ID
      pendingMeetingId: null,
      setPendingMeetingId: (id) => set({ pendingMeetingId: id }),

      // Favorites map visibility
      showFavoritesMap: false,
      setShowFavoritesMap: (show) => set({ showFavoritesMap: show }),

      // Theme mode: 'auto' by default (switches based on time of day)
      themeMode: 'auto' as 'light' | 'dark' | 'auto',
      setThemeMode: (mode) => {
        const isNightTime = () => {
          const hour = new Date().getHours();
          return hour < 6 || hour >= 18;
        };
        const newDarkMode = mode === 'auto' ? isNightTime() : mode === 'dark';
        set({ themeMode: mode, darkMode: newDarkMode });
      },

      // Helper to check if it's night time (6pm-6am)
      isNightTime: () => {
        const hour = new Date().getHours();
        return hour < 6 || hour >= 18; // Before 6am or after 6pm
      },

      // darkMode is computed from themeMode
      darkMode: (() => {
        const hour = new Date().getHours();
        return hour < 6 || hour >= 18; // Default to auto mode behavior
      })(),

      // Simple toggle: light ↔ dark (no auto cycling - just flip the mode)
      toggleDarkMode: () => set((state) => {
        // If currently dark, go to light. If light or auto, go to dark.
        const newDarkMode = !state.darkMode;
        const newMode = newDarkMode ? 'dark' : 'light';
        return { themeMode: newMode, darkMode: newDarkMode };
      }),

      // High contrast mode (default ON for oilfield use)
      highContrast: true,
      toggleHighContrast: () => set((state) => ({ highContrast: !state.highContrast })),

      // Language (default to English)
      language: 'en',
      setLanguage: (language) => set({ language }),
      toggleLanguage: () => set((state) => ({ language: state.language === 'en' ? 'es' : 'en' })),

      // Location sharing mode (default to while using app)
      locationSharingMode: 'while_using',
      setLocationSharingMode: (mode) => set({ locationSharingMode: mode }),

      // User vehicle type (default to GPS dot)
      userVehicle: 'dot',
      setUserVehicle: (vehicle) => set({ userVehicle: vehicle }),
    }),
    {
      name: 'ziawellnav-storage',
      partialize: (state) => ({
        favorites: state.favorites,
        selectedCounty: state.selectedCounty,
        subscriptionTier: state.subscriptionTier,
        voiceFeedbackEnabled: state.voiceFeedbackEnabled,
        themeMode: state.themeMode,
        highContrast: state.highContrast,
        language: state.language,
        locationSharingMode: state.locationSharingMode,
        userVehicle: state.userVehicle,
      }),
      // Recalculate darkMode after rehydration based on persisted themeMode
      onRehydrateStorage: () => (state) => {
        if (state) {
          const isNightTime = () => {
            const hour = new Date().getHours();
            return hour < 6 || hour >= 18;
          };
          const newDarkMode = state.themeMode === 'auto' ? isNightTime() : state.themeMode === 'dark';
          // Directly update the darkMode in the store after rehydration
          useAppStore.setState({ darkMode: newDarkMode });
        }
      },
    }
  )
);

// Helper hook to get computed darkMode based on themeMode
// Use this instead of directly accessing state.darkMode
export const useDarkMode = () => {
  const themeMode = useAppStore((state) => state.themeMode);
  const isNightTime = () => {
    const hour = new Date().getHours();
    return hour < 6 || hour >= 18;
  };
  return themeMode === 'auto' ? isNightTime() : themeMode === 'dark';
};
